import { data, type ObservableValue } from '@amazon/vinyl-observable'
import type { Maybe } from '@amazon/vinyl-util'
import type { CreateCloudQueueOptions } from './cloudQueueOptions'
import { isFilteredByExplicit } from './explicitFilter'
import type { PlaylistDataProvider, PlaylistEntry, RepeatMode } from './PlaylistController'

/** A fixed list of entries, walked in order from `startIndex` without a cloud queue. */
export interface StaticFeedSpec {
    readonly entries: readonly PlaylistEntry[]
    readonly startIndex?: number
    /** Marks the feed as this container's, so its play control reads it as live. */
    readonly cloudQueueOptions?: Maybe<CreateCloudQueueOptions>
}

/** The play modes a static feed walks by, read on every step. */
export interface StaticFeedModes {
    readonly shuffle: () => boolean
    readonly repeat: () => RepeatMode
}

/** A static feed that takes entries after it started, and publishes the order it plays them in. */
export interface StaticFeed extends PlaylistDataProvider {
    readonly order$: ObservableValue<readonly PlaylistEntry[]>
    /** Plays `entry` right after `current` (`next`) or after the feed's last entry (`last`). */
    add(entry: PlaylistEntry, at: 'next' | 'last', current: PlaylistEntry): void
}

export function isStaticFeed(provider: PlaylistDataProvider): provider is StaticFeed {
    return 'order$' in provider
}

function inserted(
    list: readonly PlaylistEntry[],
    entry: PlaylistEntry,
    at: 'next' | 'last',
    current: PlaylistEntry
): readonly PlaylistEntry[] {
    const index = list.indexOf(current)
    return at === 'next' && index >= 0
        ? [...list.slice(0, index + 1), entry, ...list.slice(index + 1)]
        : [...list, entry]
}

function shuffled<T>(items: readonly T[]): T[] {
    const out = [...items]
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[out[i], out[j]] = [out[j]!, out[i]!]
    }
    return out
}

/**
 * Walks `entries` from `startIndex`, stepping past those the explicit filter holds back. As a
 * cloud queue does, shuffle keeps the entry it is turned on from and shuffles every other after
 * it, turning it off resumes the list's own order from there, and repeat all wraps past the end.
 */
export function staticFeed(spec: StaticFeedSpec, modes: StaticFeedModes): StaticFeed {
    const { startIndex = 0 } = spec
    let entries = spec.entries
    let order: readonly PlaylistEntry[] = entries
    let orderShuffled = false
    const order$ = data(order)

    function orderFrom(current: PlaylistEntry): readonly PlaylistEntry[] {
        const shuffle = modes.shuffle()
        if (shuffle !== orderShuffled) {
            orderShuffled = shuffle
            order = shuffle
                ? [current, ...shuffled(entries.filter((entry) => entry !== current))]
                : entries
            order$.value = order
        }
        return order
    }

    const playable = (list: readonly PlaylistEntry[], from: number, step: 1 | -1) => {
        for (let index = from; index >= 0 && index < list.length; index += step) {
            if (!isFilteredByExplicit(list[index])) return list[index]!
        }
        return null
    }

    return {
        order$,
        add: (entry, at, current) => {
            entries = inserted(entries, entry, at, current)
            order = orderShuffled ? inserted(order, entry, at, current) : entries
            order$.value = order
        },
        cloudQueueOptions: spec.cloudQueueOptions ?? null,
        next: async (current) => {
            if (current == null) return playable(entries, startIndex, 1)
            const list = orderFrom(current)
            const index = list.indexOf(current)
            if (index < 0) return null
            const next = playable(list, index + 1, 1)
            return next ?? (modes.repeat() === 'all' ? playable(list, 0, 1) : null)
        },
        previous: async (current) => {
            const list = orderFrom(current)
            return playable(list, list.indexOf(current) - 1, -1)
        },
    }
}
